import {
  Component,
  output,
  signal,
  type TemplateRef,
  ViewChild,
  inject,
  input,
} from '@angular/core';
import { Router } from '@angular/router';

import { GuardedFeature } from '@core/feature-guard-legacy';
import { I18nService, TranslatePipe } from '@core/i18n-legacy';
import type { ConnectedOverlayDirective, IconName } from '@core/user-interface-legacy';
import {
  DropdownComponent,
  DropdownHeaderComponent,
  KeyCode,
  NestedTemplateTriggerDirective,
  ScrollableDirective,
} from '@core/user-interface-legacy';

import type { RedactorInsertsStep } from '../../../redactor/types';
import type { WysiwygLink } from '../../../wysiwyg-extensions/link/wysiwyg-link.types';
import {
  KnownInserts,
  RedactorInsertsComponentOutletComponent,
} from '../redactor-inserts-contract';
import { RedactorInsertsDropdownItemComponent } from '../redactor-inserts-dropdown-item/redactor-inserts-dropdown-item.component';

@Component({
  selector: 'rt-redactor-inserts-dropdown',
  templateUrl: 'redactor-inserts-dropdown.component.html',
  styleUrls: ['./redactor-inserts-dropdown.component.less'],
  host: {
    '(window:keydown)': 'handleKeyDown($event)',
  },
  imports: [
    DropdownComponent,
    ScrollableDirective,
    DropdownHeaderComponent,
    RedactorInsertsDropdownItemComponent,
    NestedTemplateTriggerDirective,
    TranslatePipe,
    RedactorInsertsComponentOutletComponent,
  ],
})
export class RedactorInsertsDropdownComponent {
  private readonly i18n = inject(I18nService);
  private readonly router = inject(Router);

  public readonly KnownInserts = KnownInserts;

  public readonly inserts = input<RedactorInsertsStep[]>(undefined!);
  public readonly emailTemplateInsideTemplate = input<TemplateRef<void>>();
  public readonly dropdownRef = input<ConnectedOverlayDirective>(undefined!);
  public readonly canManageCandidates = input<boolean>(undefined!);

  public emailTemplateChoose = output<unknown>();
  public insertLink = output<WysiwygLink>();
  public saveAsTemplate = output<void>();

  @ViewChild('emailTemplates', { static: true }) public emailTemplates: TemplateRef<void>;
  @ViewChild('gdprLinks', { static: true }) public gdprLinks: TemplateRef<void>;
  @ViewChild('gdprLinksWithoutConsent', { static: true })
  public gdprLinksWithoutConsent: TemplateRef<void>;
  @ViewChild('questionnairesTemplates', { static: true })
  public questionnairesTemplates: TemplateRef<void>;
  @ViewChild('schedules', { static: true })
  public schedules: TemplateRef<void>;
  @ViewChild('eventInvitationTemplates', { static: true })
  public eventInvitationTemplates: TemplateRef<void>;
  @ViewChild('integrationsKununu', { static: true })
  public integrationsKununu: TemplateRef<void>;
  @ViewChild('dropdown', { static: true }) public dropdown: DropdownComponent;

  public selectedIndex = signal<number | null>(null);
  public hasBackAction = signal(false);

  public RedactorInsertStepTemplateMap: Record<
    RedactorInsertsStep,
    {
      template: TemplateRef<void>;
      icon: IconName;
      heading: string;
      subheading: string;
      featureGuard?: GuardedFeature;
    }
  >;

  public ngOnInit(): void {
    this.hasBackAction.set(this.inserts().length !== 1);
    this.setTemplateMap();
  }

  public ngAfterViewInit(): void {
    const hasFixedStep = this.inserts().length === 1;

    if (hasFixedStep) {
      this.setStep(this.inserts()[0]);
    }
  }

  public handleKeyDown(event: KeyboardEvent): void {
    const key = event.which || event.keyCode;

    switch (key) {
      case KeyCode.Escape:
        this.closeDropdown();
        break;

      case KeyCode.Enter: {
        event.preventDefault();
        event.stopPropagation();
        const selectedIndex = this.selectedIndex();
        if (selectedIndex === null) {
          this.closeDropdown();
        } else {
          this.setStep(this.inserts()[selectedIndex]);
        }
        break;
      }

      case KeyCode.UpArrow: {
        event.preventDefault();
        const selectedIndex = this.selectedIndex();
        if (selectedIndex === 0 || selectedIndex === null) {
          this.selectedIndex.set(this.inserts().length - 1);
        } else {
          this.selectedIndex.update(v => v! - 1);
        }
        break;
      }

      case KeyCode.DownArrow: {
        event.preventDefault();
        const selectedIndex = this.selectedIndex();
        if (selectedIndex === this.inserts().length - 1 || selectedIndex === null) {
          this.selectedIndex.set(0);
        } else {
          this.selectedIndex.update(v => v! + 1);
        }
        break;
      }

      default:
        return;
    }
  }

  public isSelected(insert: RedactorInsertsStep): boolean {
    return this.inserts().indexOf(insert) === this.selectedIndex();
  }

  public setStep(step: RedactorInsertsStep): void {
    this.dropdown.setNestedTemplate(this.RedactorInsertStepTemplateMap[step].template);
    this.selectedIndex.set(null);
  }

  public goToSettings(url: string): void {
    this.router.navigate([url]);
  }

  public closeDropdown(): void {
    this.dropdownRef().open = false;
  }

  private setTemplateMap() {
    this.RedactorInsertStepTemplateMap = {
      'email-templates': {
        template: this.emailTemplates,
        icon: 'mail',
        heading: this.i18n.translate('app.redactor_inserts.email_templates.dropdown.heading'),
        subheading: this.i18n.translate('app.redactor_inserts.email_templates.dropdown.subheading'),
      },
      'gdpr-links': {
        template: this.gdprLinks,
        icon: 'gdpr-candidate',
        heading: this.i18n.translate('app.redactor_inserts.gdpr.dropdown.heading'),
        subheading: this.i18n.translate('app.redactor_inserts.gdpr.dropdown.subheading'),
      },
      'gdpr-links-without-consent': {
        template: this.gdprLinksWithoutConsent,
        icon: 'gdpr-candidate',
        heading: this.i18n.translate('app.redactor_inserts.gdpr.dropdown.heading'),
        subheading: this.i18n.translate('app.redactor_inserts.gdpr.dropdown.subheading'),
      },
      'questionnaires-templates': {
        template: this.questionnairesTemplates,
        icon: 'list',
        heading: this.i18n.translate('app.redactor_inserts.questionnaires.dropdown.heading'),
        subheading: this.i18n.translate('app.redactor_inserts.questionnaires.dropdown.subheading'),
        featureGuard: GuardedFeature.Questionnaires,
      },
      schedules: {
        template: this.schedules,
        icon: 'access-time',
        heading: this.i18n.translate('app.redactor_inserts.scheduler.dropdown.heading'),
        subheading: this.i18n.translate('app.redactor_inserts.scheduler.dropdown.subheading'),
        featureGuard: GuardedFeature.Scheduler,
      },
      'event-invitation-templates': {
        template: this.eventInvitationTemplates,
        icon: 'mail',
        heading: this.i18n.translate('app.redactor_inserts.email_templates.dropdown.heading'),
        subheading: this.i18n.translate('app.redactor_inserts.email_templates.dropdown.subheading'),
      },
      'integrations-kununu': {
        template: this.integrationsKununu,
        icon: 'rti-kununu',
        heading: this.i18n.translate('app.redactor_inserts.integrations_kununu.dropdown.heading'),
        subheading: this.i18n.translate(
          'app.redactor_inserts.integrations_kununu.dropdown.subheading',
        ),
      },
    };
  }
}
